/**
 * Accessibility Fixes for Third-Party Libraries
 * 
 * This script automatically wraps third-party widgets and libraries
 * in proper ARIA landmarks to meet accessibility requirements.
 * 
 * Fixes:
 * - Google Translate widget
 * - Other third-party widgets that lack proper ARIA landmarks
 */

class AccessibilityFixes {
    constructor() {
        this.initialized = false;
        
        // Configuration for different widget types
        this.WIDGET_CONFIGS = {
            gtranslate: {
                selector: '.gtranslate_wrapper',
                landmark: 'navigation',
                label: 'Language Selector',
                wrapper: '.gt_switcher_wrapper'
            },
            // Add more widget configurations as needed
            // Example for other widgets:
            // socialWidget: {
            //     selector: '.social-widget',
            //     landmark: 'complementary',
            //     label: 'Social Media Links',
            //     wrapper: '.social-links'
            // }
        };

        // Configuration for iframe accessibility fixes
        this.IFRAME_FIXES = {
            peek: {
                selector: '#peek-pseudo-frame',
                title: 'Peek Contact Widget',
                description: 'Third-party contact widget for property inquiries'
            },
            sightmap: {
                selector: '#sightmap',
                title: 'Interactive Property Map',
                description: 'Interactive map showing property locations and details'
            }
        };

        // Configuration for button accessibility fixes
        this.BUTTON_FIXES = {
            gmpAdvancedMarker: {
                selector: 'gmp-advanced-marker[role="button"]',
                ariaLabel: 'Property location marker',
                title: 'Click to view property details'
            }
        };
    }

    /**
     * Creates an accessible landmark wrapper
     * @param {string} role - ARIA role (navigation, complementary, etc.)
     * @param {string} label - Accessible label
     * @returns {HTMLElement} - Wrapped element
     */
    createLandmarkWrapper(role, label) {
        const wrapper = document.createElement('div');
        wrapper.setAttribute('role', role);
        wrapper.setAttribute('aria-label', label);
        wrapper.setAttribute('aria-live', 'polite');
        return wrapper;
    }

    /**
     * Fixes accessibility for a specific widget type
     * @param {string} widgetType - Type of widget to fix
     * @param {Object} config - Configuration for the widget
     */
    fixWidgetAccessibility(widgetType, config) {
        const widgets = document.querySelectorAll(config.selector);
        
        widgets.forEach((widget, index) => {
            // Skip if already processed
            if (widget.hasAttribute('data-accessibility-fixed')) {
                return;
            }

            const wrapperElement = widget.querySelector(config.wrapper);
            if (!wrapperElement) {
                console.warn(`Accessibility fix: Could not find wrapper element for ${widgetType}`);
                return;
            }

            // Create landmark wrapper
            const landmarkWrapper = this.createLandmarkWrapper(config.landmark, config.label);
            
            // Insert landmark wrapper before the widget content
            widget.insertBefore(landmarkWrapper, wrapperElement);
            
            // Move the widget content into the landmark wrapper
            landmarkWrapper.appendChild(wrapperElement);
            
            // Mark as processed
            widget.setAttribute('data-accessibility-fixed', 'true');
            
            console.log(`Accessibility fix applied to ${widgetType} widget ${index + 1}`);
        });
    }

    /**
     * Fixes iframe accessibility by adding proper title and aria-label attributes
     * @param {string} iframeType - Type of iframe to fix
     * @param {Object} config - Configuration for the iframe
     */
    fixIframeAccessibility(iframeType, config) {
        const iframes = document.querySelectorAll(config.selector);
        
        iframes.forEach((iframe, index) => {
            // Skip if already processed
            if (iframe.hasAttribute('data-accessibility-fixed')) {
                return;
            }

            // Add title attribute for screen readers
            if (config.title) {
                iframe.setAttribute('title', config.title);
            }

            // Add aria-label for additional context
            if (config.description) {
                iframe.setAttribute('aria-label', config.description);
            }

            // Mark as processed
            iframe.setAttribute('data-accessibility-fixed', 'true');
            
            console.log(`Iframe accessibility fix applied to ${iframeType} iframe ${index + 1}`);
        });
    }

    /**
     * Fixes button accessibility by adding proper aria-label and title attributes
     * @param {string} buttonType - Type of button to fix
     * @param {Object} config - Configuration for the button
     */
    fixButtonAccessibility(buttonType, config) {
        const buttons = document.querySelectorAll(config.selector);
        
        buttons.forEach((button, index) => {
            // Skip if already processed
            if (button.hasAttribute('data-accessibility-fixed')) {
                return;
            }

            // For Google Maps markers, try to get the visible text content
            if (buttonType === 'gmpAdvancedMarker') {
                const textElement = button.querySelector('text');
                if (textElement) {
                    const visibleText = textElement.textContent || textElement.innerText;
                    if (visibleText && visibleText.trim()) {
                        // Use the visible text as the accessible name
                        button.setAttribute('aria-label', `Property marker ${visibleText.trim()}`);
                        button.setAttribute('title', `Click to view property ${visibleText.trim()}`);
                    } else {
                        // Fallback to generic label
                        button.setAttribute('aria-label', config.ariaLabel);
                        button.setAttribute('title', config.title);
                    }
                } else {
                    // Fallback to generic label
                    button.setAttribute('aria-label', config.ariaLabel);
                    button.setAttribute('title', config.title);
                }
            } else {
                // For other buttons, use the configured labels
                if (config.ariaLabel) {
                    button.setAttribute('aria-label', config.ariaLabel);
                }
                if (config.title) {
                    button.setAttribute('title', config.title);
                }
            }

            // Mark as processed
            button.setAttribute('data-accessibility-fixed', 'true');
            
            console.log(`Button accessibility fix applied to ${buttonType} button ${index + 1}`);
        });
    }

    /**
     * Special method to handle Google Maps markers that load very late
     */
    setupGoogleMapsObserver() {
        // Watch specifically for Google Maps container changes
        const mapContainers = document.querySelectorAll('[id*="map"], [class*="map"], [id*="sightmap"]');
        
        mapContainers.forEach(container => {
            const observer = new MutationObserver((mutations) => {
                let shouldCheck = false;
                
                mutations.forEach((mutation) => {
                    if (mutation.addedNodes.length > 0) {
                        mutation.addedNodes.forEach((node) => {
                            if (node.nodeType === Node.ELEMENT_NODE) {
                                // Check for Google Maps markers
                                if (node.matches && node.matches('gmp-advanced-marker[role="button"]')) {
                                    shouldCheck = true;
                                } else if (node.querySelector && node.querySelector('gmp-advanced-marker[role="button"]')) {
                                    shouldCheck = true;
                                }
                            }
                        });
                    }
                });
                
                if (shouldCheck) {
                    // Small delay to ensure markers are fully rendered
                    setTimeout(() => this.fixButtonAccessibility('gmpAdvancedMarker', this.BUTTON_FIXES.gmpAdvancedMarker), 100);
                }
            });

            observer.observe(container, {
                childList: true,
                subtree: true
            });
        });

        // Also listen for Google Maps API events if available
        if (window.google && window.google.maps) {
            // Listen for map idle event
            const checkForMarkers = () => {
                setTimeout(() => {
                    this.fixButtonAccessibility('gmpAdvancedMarker', this.BUTTON_FIXES.gmpAdvancedMarker);
                }, 500);
            };

            // Check when Google Maps API is loaded
            if (window.google.maps.event) {
                window.google.maps.event.addListenerOnce(window.google.maps, 'idle', checkForMarkers);
            }
        }

        // Additional fallback: check for markers every 2 seconds for the first 30 seconds
        let googleMapsCheckCount = 0;
        const googleMapsInterval = setInterval(() => {
            googleMapsCheckCount++;
            this.fixButtonAccessibility('gmpAdvancedMarker', this.BUTTON_FIXES.gmpAdvancedMarker);
            
            if (googleMapsCheckCount >= 15) { // Stop after 30 seconds (15 * 2 seconds)
                clearInterval(googleMapsInterval);
            }
        }, 2000);
    }

    /**
     * Main function to apply all accessibility fixes
     */
    applyAccessibilityFixes() {
        // Fix widget accessibility
        Object.keys(this.WIDGET_CONFIGS).forEach(widgetType => {
            const config = this.WIDGET_CONFIGS[widgetType];
            this.fixWidgetAccessibility(widgetType, config);
        });

        // Fix iframe accessibility
        Object.keys(this.IFRAME_FIXES).forEach(iframeType => {
            const config = this.IFRAME_FIXES[iframeType];
            this.fixIframeAccessibility(iframeType, config);
        });

        // Fix button accessibility
        Object.keys(this.BUTTON_FIXES).forEach(buttonType => {
            const config = this.BUTTON_FIXES[buttonType];
            this.fixButtonAccessibility(buttonType, config);
        });
    }

    /**
     * Enhanced function to handle dynamically loaded content
     */
    setupDynamicContentObserver() {
        const observer = new MutationObserver((mutations) => {
            let shouldCheck = false;
            
            mutations.forEach((mutation) => {
                if (mutation.addedNodes.length > 0) {
                    // Check if any new nodes contain our target widgets or iframes
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            // Check if the node itself is a widget or contains widgets
                            Object.keys(this.WIDGET_CONFIGS).forEach(widgetType => {
                                const config = this.WIDGET_CONFIGS[widgetType];
                                if (node.matches && node.matches(config.selector)) {
                                    shouldCheck = true;
                                } else if (node.querySelector && node.querySelector(config.selector)) {
                                    shouldCheck = true;
                                }
                            });

                            // Check if the node itself is an iframe or contains iframes
                            Object.keys(this.IFRAME_FIXES).forEach(iframeType => {
                                const config = this.IFRAME_FIXES[iframeType];
                                if (node.matches && node.matches(config.selector)) {
                                    shouldCheck = true;
                                } else if (node.querySelector && node.querySelector(config.selector)) {
                                    shouldCheck = true;
                                }
                            });

                            // Check if the node itself is a button or contains buttons
                            Object.keys(this.BUTTON_FIXES).forEach(buttonType => {
                                const config = this.BUTTON_FIXES[buttonType];
                                if (node.matches && node.matches(config.selector)) {
                                    shouldCheck = true;
                                } else if (node.querySelector && node.querySelector(config.selector)) {
                                    shouldCheck = true;
                                }
                            });
                        }
                    });
                }
            });
            
            if (shouldCheck) {
                // Small delay to ensure DOM is fully updated
                setTimeout(() => this.applyAccessibilityFixes(), 100);
            }
        });

        // Start observing
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        return observer;
    }

    /**
     * Initialize accessibility fixes
     */
    init() {
        if (this.initialized) {
            return;
        }

        // Apply fixes immediately for existing content
        this.applyAccessibilityFixes();
        
        // Set up observer for dynamically loaded content
        this.setupDynamicContentObserver();
        
        // Set up special observer for Google Maps markers
        this.setupGoogleMapsObserver();
        
        // Also run periodically as a fallback (for very dynamic content like Google Maps)
        const fallbackInterval = setInterval(() => {
            this.applyAccessibilityFixes();
        }, 1000); // Check every second for Google Maps markers
        
        // Clean up after 30 seconds to prevent unnecessary processing
        setTimeout(() => {
            clearInterval(fallbackInterval);
        }, 30000);
        
        this.initialized = true;
        console.log('Accessibility fixes initialized');
    }
}

// Create and export instance
const accessibilityFixes = new AccessibilityFixes();

// Wait for DOM to be ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => accessibilityFixes.init());
} else {
    // DOM is already ready
    accessibilityFixes.init();
}

// Also run when window loads (for very late-loading content)
window.addEventListener('load', () => {
    setTimeout(() => accessibilityFixes.applyAccessibilityFixes(), 500);
});

// Expose function globally for manual triggering if needed
window.fixAccessibility = () => accessibilityFixes.applyAccessibilityFixes();

export default accessibilityFixes;